import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import TreeView from 'react-accessible-treeview';
import { Form } from 'react-bootstrap';

const Index = ({
  nodes,
  treeNodeHandler,
  parentNodesCount,
  selectedChildIds,
  setSelectedChildIds,
  treeIndex,
  getStandardDetails
}) => {
  const [selectedParentNodes, setSelectedParentNodes] = useState([]);
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [isExpand, setIsExpand] = useState(false);
  const [flatTree, setFlatTree] = useState([]);
  
  useEffect(() => {
    if (!nodes?.length) {
      setFlatTree([]);
      return;
    }

    const result = [];
    nodes.forEach(node => buildFlatTree(node, result));

    setFlatTree(result);
  }, [nodes]);

  const buildFlatTree = (node, result) => {
    if (node?.childrens?.length) {
      result.push(
        ...node.childrens.map((child) => ({
          ...child,
          parentId: node.id,
        }))
      );
  
      node.childrens.forEach((child) => {
        buildFlatTree(child, result);
      });
    }
  };

  const handleMainParentClick = (nodeId, index) => {
    if (!selectedParentNodes.some((node) => node?.id === nodeId)) {
      let newNode = { id: nodeId };
      let newSelectedNodes = [...selectedParentNodes];
      newSelectedNodes = Array.from({ length: parentNodesCount }, (_, i) => ({
        id: i < selectedParentNodes.length ? selectedParentNodes[i].id : ''
      }));
      newSelectedNodes[index] = { id: newNode.id };
      setSelectedParentNodes(newSelectedNodes);
    } else {
      setSelectedParentNodes((prevNodes) =>
        prevNodes.map((item, i) => ({
          id: i === index ? '' : item.id
        }))
      );
    }
    setIsExpand(!isExpand);
  };

  const handleChildClick = (rootName, nodeValue, isExpanded, isChildAvailable, childrens) => {
    if (!isExpanded) treeNodeHandler(rootName, nodeValue, isChildAvailable, childrens, treeIndex);
  };

  const handleChildCheckboxSelect = (event) => {
    const nodeId = event.target.id;
    const updatedChildIds = event.target.checked
      ? Array.from(new Set([
        ...selectedChildIds,
        nodeId
      ]))
      : selectedChildIds.filter(id => id !== nodeId);

    setSelectedChildIds(updatedChildIds);

    const updatedParentIds = getSelectedParentIds(updatedChildIds);

    setSelectedParentIds(updatedParentIds);
  };

  const getSelectedParentIds = (selectedChildIds) => {
    const selectedIds = new Set(selectedChildIds);
    const parentIds = [];
    const fullySelectedCache = new Map();

    const isFullySelected = (node) => {
      if (!node) {
        return false;
      }

      if (fullySelectedCache.has(node.id)) {
        return fullySelectedCache.get(node.id);
      }

      let result;

      if (!node.childrens?.length) {
        result = selectedIds.has(node.id);
      } else {
        result = node.childrens.every(child =>
          isFullySelected(child)
        );
      }

      fullySelectedCache.set(node.id, result);

      return result;
    };

    const checkNode = (node) => {
      if (!node?.childrens?.length) {
        return;
      }

      if (isFullySelected(node)) {
        parentIds.push(node.id);
      }

      node.childrens.forEach(child => {
        checkNode(child);
      });
    };

    nodes.forEach(mainParent => {
      checkNode(mainParent);

      const directChildren =
      mainParent.childrens?.filter(
        child => child.id != mainParent.id
      ) || [];

      const allChildrenSelected =
      directChildren.length > 0 &&
      directChildren.every(child =>
        isFullySelected(child)
      );

      if (allChildrenSelected) {
        parentIds.push(mainParent.id);
      }
    });

    return [...new Set(parentIds)];
  };

  const handleParentCheckBoxSelect = (nodeId, checked) => {
    const selectedNode =
    nodes.find(node => node.id == nodeId) ||
    flatTree.find(item => item.id == nodeId);
    let childIds = getAllChildIds(selectedNode);

    const updatedChildIds = checked
      ? Array.from(new Set([...selectedChildIds, ...childIds]))
      : selectedChildIds.filter(id => !childIds.includes(id));

    setSelectedChildIds(updatedChildIds);

    const updatedParentIds = getSelectedParentIds(updatedChildIds);

    setSelectedParentIds(updatedParentIds);
  };

  const getAllChildIds = (node) => {
    let childNodes = [];
    const collectChildren = (node) => {
      if (node?.childrens?.length) {
        node.childrens.forEach((child) => {
          childNodes.push(child.id);
          collectChildren(child);
        });
      }
    };
    collectChildren(node);
    return childNodes;
  };

  return (
    <>
      {nodes.map((item, index) => {
        return (
          <div
            key={index}
            className={`assignments-card col-4 cursor-pointer w-100 ${index % 3 === 0 ? 'clear-row' : ''}`}>
            <div className="select-questions p-1">
              <div
                id={item.name}
                className="d-flex align-items-center">
                <div className="align-items-center d-flex tree-icon-div justify-content-center m-2"
                  onClick={() => handleMainParentClick(item.name, index)}>
                  <FontAwesomeIcon
                    icon={
                      selectedParentNodes.find((node) => node.id === item.name) ? faMinus : faPlus
                    }
                    className="tree-icon"
                  />
                </div>
                <Form.Check
                  type="checkbox"
                  checked={(selectedParentIds.filter((obj) => obj == item.id)).length > 0}
                  className="checkbox-name mr-1"
                  onChange={(e) => handleParentCheckBoxSelect(item.id, e.target.checked)}
                />
                <div>{item.name}</div>
              </div>
              {selectedParentNodes.find((node) => node.id === item.name) && (
                <>
                  <TreeView
                    key={`tree${index}`}
                    data={item.childrens}
                    aria-label="Checkbox tree"
                    multiSelect
                    propagateSelect
                    propagateSelectUpwards
                    togglableSelect
                    nodeRenderer={({
                      element,
                      isBranch,
                      isExpanded,
                      getNodeProps,
                      handleExpand
                    }) => {
                      return (
                        <>
                          <div>
                            {isBranch && (
                              <>
                                <div>
                                  <div className='d-flex'>
                                    <div className="tree-icon-div mb-2 margin-left-20"
                                      onClick={() =>
                                        handleChildClick(item.name, element.id, isExpanded, element.isChildAvailable, element.childrens)
                                      }>
                                      <div {...getNodeProps({ onClick: handleExpand })}>
                                        <FontAwesomeIcon
                                          icon={isExpanded ? faMinus : faPlus}
                                          className="tree-icon"
                                          isOpen={isExpanded}
                                          
                                        />
                                      </div>
                                    </div>
                                    <Form.Check
                                      type="checkbox"
                                      checked={(selectedParentIds.filter((obj) => obj == element.id)).length > 0}
                                      className="checkbox-name"
                                      onChange={(e) => handleParentCheckBoxSelect(element.id, e.target.checked)}
                                    />
                                    <span id={element.id} className="inkto m-2 mb-1 mt-0">
                                      {element.name} {element.showLabel && <label className='text-info text-decoration-underline cursor-pointer' onClick={()=>getStandardDetails(element.id, true, flatTree)}>Show all standards</label>}
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}
                            {!isBranch && (
                              <div className="d-flex">
                                <Form.Check
                                  id={element.id}
                                  type="checkbox"
                                  className="checkbox-name margin-left-20"
                                  onChange={(e) => {
                                    handleChildCheckboxSelect(e);
                                    e.stopPropagation();
                                  }}
                                  value={element.name}
                                  checked={(selectedChildIds.filter((obj) => obj == element.id)).length > 0}
                                />
                                <span
                                  id={element.id}
                                  className="m-2 mb-1 mt-0">
                                  {element.name} {element.showLabel && <label className='text-info text-decoration-underline cursor-pointer' onClick={()=>getStandardDetails(element.id, false)}>Show Standard</label>}
                                </span>
                              </div>
                            )}
                          </div>
                        </>
                      );
                    }}
                  />
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

Index.propTypes = {
  nodes: PropTypes.array,
  treeNodeHandler: PropTypes.func,
  parentNodesCount: PropTypes.number,
  selectedAllNodes: PropTypes.array,
  setSelectedAllNodes: PropTypes.func,
  setSelectedNodes: PropTypes.func,
  treeIndex: PropTypes.number,
  selectedChildIds: PropTypes.array,
  setSelectedChildIds: PropTypes.func,
  getStandardDetails: PropTypes.func
};

export default Index;
